// Bootstrap-time API/ID endpoint resolution for the customer app (DEV-1057).
//
// Runs in the thin `main.tsx` entry BEFORE the render module is dynamically
// imported, so `config.ts`'s `API_URL`/`ID_URL` consts freeze at the resolved
// value. On a Pages preview host both endpoints resolve to PROD; off-preview it
// is a no-op (the store stays null) and `config.ts` uses env/prod.
//
// WHY NOT THE PER-PR API STAND (the stand-capture this used to do).
//
// This module previously health-probed `https://symoditi-api-<slug>.fly.dev` and,
// when `/health/ready` answered, pointed BOTH `apiUrl` and `idUrl` at it. That
// made a liveness probe confer the OAuth ISSUER role on a host we do not own:
//
//  - `<slug>` comes from the branch name, and for a frontend-only PR no stand is
//    ever created — the derived host is NXDOMAIN. Fly app names are a single
//    global namespace, claimable by any Fly user, so anyone could register
//    `symoditi-api-<slug>.fly.dev`, answer the probe, and the preview frontend
//    would send our users to THEIR login page. A credential-phishing vector
//    gated on nothing but "something responded 200".
//
//  - The half-measure (`apiUrl` = stand, `idUrl` = prod) does not work either,
//    so it is not an option we gave up: the SPA sends `resource: API_URL` on
//    authorize/token/refresh (`lib/redirect-to-login.ts`,
//    `components/auth/AuthCallback.tsx`, `lib/token-refresh.ts`). Either prod
//    refuses to mint a token for a stand audience (`checkResource` in
//    `@better-auth/oauth-provider` rejects a `resource` outside `validAudiences`
//    with `invalid_request`), or — pointing the issuer at the stand — the token
//    carries the prod audience and the stand rejects it (a stand leaves
//    `API_RESOURCE_URL` unset, so `restAcceptedAudiences` accepts exactly its own
//    `PUBLIC_URL`).
//
// So until a stand can serve its own login page AND live on a domain we control,
// prod-for-both is the only configuration in which preview login works at all.
// The pure helpers (`isAppPreviewHost`, `deriveAppApiStandUrl`) are deliberately
// kept — that follow-up needs them.

import { isAppPreviewHost, setResolvedEndpoints } from './runtime-endpoints';

const PROD_API_URL = 'https://api.symoditi.com';
const PROD_ID_URL = 'https://id.symoditi.com';

/**
 * Resolve and store the runtime API/ID endpoints. Never throws.
 *
 * - Off a preview host: no-op — the store stays null and `config.ts` falls back
 *   to the build-time `VITE_API_URL` (local dev) or the prod default.
 * - On a preview host: ALWAYS populates the store with prod, so a stale CF
 *   `VITE_API_URL` preview var can never win. No network call, no probe delay.
 *
 * Async only to keep the `main.tsx` bootstrap contract (`await` before the
 * render module is imported) — it resolves immediately.
 */
export async function resolveAndStoreEndpoints(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): Promise<void> {
  if (!isAppPreviewHost(hostname)) return;
  setResolvedEndpoints({ apiUrl: PROD_API_URL, idUrl: PROD_ID_URL });
}
